import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../contexts/UserContext';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  User,
  DollarSign,
  FileText,
  Eye,
  Calendar,
  Upload,
  Sparkles,
  Users as UsersIcon,
  Building,
  AlertCircle,
  Clock,
  Download,
  CheckCircle,
  Search,
  ChevronDown
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const ContractWizard = ({ contract, onClose, onSave }) => {
  const { user } = useContext(UserContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestedNumber, setSuggestedNumber] = useState('');
  const [previousRate, setPreviousRate] = useState(null);
  const [createdContractId, setCreatedContractId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    user: contract?.user || '',
    company: contract?.company || user?.company || '',
    contract_number: contract?.contract_number || 'Select user to generate',
    creation_date: contract?.creation_date || new Date().toISOString().split('T')[0],
    start_date: contract?.start_date || '',
    end_date: contract?.end_date || '',
    status: contract?.status || 'active',

    // Step 2: Rate & Compensation
    current_rate: contract?.current_rate || '',
    currency: contract?.currency || 'PLN',
    rate_type: contract?.rate_type || 'hourly',
    rate_travel: contract?.rate_travel || '',
    rate_training: contract?.rate_training || '',
    custom_rates: contract?.custom_rates || {},

    // Employment details
    job_position: contract?.job_position || '',
    work_location: contract?.work_location || '',
    working_hours: contract?.working_hours || '',
    vacation_days: contract?.vacation_days || '',
    notice_period: contract?.notice_period || '',
    bank_name: contract?.bank_name || '',
    bank_account: contract?.bank_account || '',

    // Step 3: Contract Text & Signatures
    contract_text_pl: contract?.contract_text_pl || '',
    contract_text_en: contract?.contract_text_en || '',
    employer_signature_name: contract?.employer_signature_name || '',
    employer_signature_title: contract?.employer_signature_title || '',
    employee_signature_name: contract?.employee_signature_name || '',
    signature_date: contract?.signature_date || new Date().toISOString().split('T')[0],
    signature_place: contract?.signature_place || '',
    pdf_document: null,
    notes: contract?.notes || ''
  });

  const [errors, setErrors] = useState({});

  // User search state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  const steps = [
    { number: 1, title: 'Basic Information', icon: User },
    { number: 2, title: 'Rate & Compensation', icon: DollarSign },
    { number: 3, title: 'Content & Signatures', icon: FileText },
    { number: 4, title: 'Summary', icon: Eye }
  ];

  useEffect(() => {
    fetchUsers();
    fetchTemplates();
    // If editing contract, don't fetch new number
    // If creating new and user is pre-selected, generate number for that user
    if (!contract && formData.user) {
      fetchNextContractNumber(formData.user);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contract-templates/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setTemplates(response.data);
      // Set default template if available
      const defaultTemplate = response.data.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
      } else if (response.data.length > 0) {
        setSelectedTemplateId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(u => {
    const searchLower = userSearchTerm.toLowerCase();
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Get selected user display name
  const getSelectedUserName = () => {
    if (!formData.user) return '';
    const selectedUser = users.find(u => u.id === parseInt(formData.user));
    return selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : '';
  };

  // Handle user selection from dropdown
  const handleUserSelect = (userId) => {
    setFormData(prev => ({ ...prev, user: userId }));
    setShowUserDropdown(false);
    setUserSearchTerm('');
    if (errors.user) {
      setErrors(prev => ({ ...prev, user: null }));
    }
    // Generate contract number for selected user
    if (!contract) {
      fetchNextContractNumber(userId);
    }
  };

  const fetchNextContractNumber = async (userId = null) => {
    try {
      const params = userId ? `?user_id=${userId}` : '';
      const response = await axios.get(`${API_BASE_URL}/contracts/next-number/${params}`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setSuggestedNumber(response.data.suggested_number);
      setFormData(prev => ({ ...prev, contract_number: response.data.suggested_number }));
    } catch (error) {
      console.error('Error fetching next contract number:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));

      // Generate contract number when user is selected
      if (name === 'user' && value && !contract) {
        fetchNextContractNumber(value);
      }
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.user) newErrors.user = 'Select a user';
      if (!formData.contract_number) newErrors.contract_number = 'Contract number is required';
      if (!formData.start_date) newErrors.start_date = 'Start date is required';
      if (formData.end_date && formData.start_date) {
        if (new Date(formData.end_date) <= new Date(formData.start_date)) {
          newErrors.end_date = 'End date must be later than start date';
        }
      }
    }

    if (step === 2) {
      if (!formData.current_rate || formData.current_rate <= 0) {
        newErrors.current_rate = 'Valid rate is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);

    try {
      const submitData = new FormData();

      Object.keys(formData).forEach(key => {
        if (key === 'pdf_document') {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          }
        } else if (key === 'custom_rates') {
          // JSON field needs to be stringified
          submitData.append(key, JSON.stringify(formData[key] || {}));
        } else if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      let response;
      if (contract) {
        response = await axios.patch(
          `${API_BASE_URL}/contracts/${contract.id}/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        onSave();
      } else {
        response = await axios.post(
          `${API_BASE_URL}/contracts/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        // Show success modal with PDF download option
        setCreatedContractId(response.data.id);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        alert('Error saving contract. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSelectedUser = () => {
    return users.find(u => u.id === parseInt(formData.user));
  };

  // Auto-fill employee signature name when user is selected
  useEffect(() => {
    const selectedUser = getSelectedUser();
    if (selectedUser && !formData.employee_signature_name) {
      setFormData(prev => ({
        ...prev,
        employee_signature_name: `${selectedUser.first_name} ${selectedUser.last_name}`
      }));
    }
  }, [formData.user]);

  // Fetch previous contract rate when user is selected
  useEffect(() => {
    const fetchPreviousRate = async () => {
      if (!formData.user || contract) return; // Don't fetch if editing existing contract

      try {
        const response = await axios.get(`${API_BASE_URL}/contracts/`, {
          headers: { Authorization: `Bearer ${user.accessToken}` },
          params: { user_id: formData.user }
        });

        if (response.data && response.data.length > 0) {
          // Get the most recent contract
          const lastContract = response.data[0];
          setPreviousRate({
            rate: lastContract.current_rate,
            currency: lastContract.currency,
            rate_type: lastContract.rate_type,
            contract_number: lastContract.contract_number,
            end_date: lastContract.end_date
          });

          // Auto-suggest start date if previous contract has end date
          if (lastContract.end_date && !formData.start_date) {
            // Suggest the day after the previous contract ends
            const endDate = new Date(lastContract.end_date);
            endDate.setDate(endDate.getDate() + 1);
            const suggestedStartDate = endDate.toISOString().split('T')[0];

            setFormData(prev => ({
              ...prev,
              start_date: suggestedStartDate
            }));
          }
        } else {
          setPreviousRate(null);
        }
      } catch (error) {
        console.error('Error fetching previous contract:', error);
        setPreviousRate(null);
      }
    };

    fetchPreviousRate();
  }, [formData.user]);

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.number;
          const isCompleted = currentStep > step.number;

          return (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                      : isCompleted
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                </div>
                <div className="mt-2 text-center">
                  <div
                    className={`text-sm font-medium ${
                      isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}
                  >
                    Step {step.number}
                  </div>
                  <div className="text-xs text-gray-600 max-w-24">{step.title}</div>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-4 rounded transition-all ${
                    currentStep > step.number ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <User className="w-5 h-5" />
        Basic Information
      </h3>

      {/* User Selection with Search */}
      <div ref={userDropdownRef} className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          User *
        </label>
        <div
          onClick={() => !contract && setShowUserDropdown(!showUserDropdown)}
          className={`w-full pl-10 pr-4 py-2 border rounded-md cursor-pointer flex items-center justify-between ${
            errors.user ? 'border-red-500' : 'border-gray-300'
          } ${contract ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}`}
        >
          <div className="flex items-center">
            <UsersIcon className="absolute left-3 text-gray-400 w-4 h-4" />
            <span className={formData.user ? 'text-gray-900' : 'text-gray-400'}>
              {getSelectedUserName() || 'Select user...'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {/* Clear button - only show when user is selected and not editing */}
            {formData.user && !contract && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormData(prev => ({
                    ...prev,
                    user: '',
                    contract_number: 'Select user to generate',
                    employee_signature_name: ''
                  }));
                  setPreviousRate(null);
                  setSuggestedNumber('');
                }}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                title="Clear selection"
              >
                <X className="w-4 h-4 text-gray-500 hover:text-gray-700" />
              </button>
            )}
            {!contract && <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />}
          </div>
        </div>

        {/* Dropdown with Search */}
        {showUserDropdown && !contract && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* User List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">No users found</div>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => handleUserSelect(u.id)}
                    className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                      formData.user === u.id ? 'bg-blue-100' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{u.first_name} {u.last_name}</div>
                    <div className="text-sm text-gray-500">{u.email}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {errors.user && (
          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.user}
          </p>
        )}
      </div>

      {/* Contract Number + Creation Date */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contract Number *
          </label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                name="contract_number"
                value={formData.contract_number}
                onChange={handleChange}
                placeholder="e.g. CTR-2024-001"
                className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.contract_number ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {suggestedNumber && formData.contract_number !== suggestedNumber && (
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, contract_number: suggestedNumber }))}
                className="flex items-center gap-1 px-3 py-2 bg-purple-50 text-purple-700 border border-purple-300 rounded-md hover:bg-purple-100 text-sm"
                title="Use suggested number"
              >
                <Sparkles className="w-4 h-4" />
              </button>
            )}
          </div>
          {suggestedNumber && (
            <p className="mt-1 text-xs text-gray-500">
              Suggested: <span className="font-medium">{suggestedNumber}</span>
            </p>
          )}
          {errors.contract_number && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.contract_number}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Creation Date
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="date"
              name="creation_date"
              value={formData.creation_date}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date *
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.start_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {previousRate?.end_date && formData.start_date && (
            <p className="mt-1 text-xs text-gray-500">
              Previous contract ended: {new Date(previousRate.end_date).toLocaleDateString()}
            </p>
          )}
          {errors.start_date && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.start_date}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
            <span className="text-xs text-gray-500 ml-1">(leave blank for indefinite)</span>
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.end_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.end_date && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.end_date}
            </p>
          )}
        </div>
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="terminated">Terminated</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Pending - for contracts prepared in advance
        </p>
      </div>

    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <DollarSign className="w-5 h-5" />
        Rate & Compensation
      </h3>

      {/* Previous Rate Info */}
      {previousRate && (
        <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
          <div className="text-gray-700">
            Previous rate ({previousRate.contract_number}): <strong>{previousRate.rate} {previousRate.currency}</strong> - {previousRate.rate_type === 'hourly' ? 'hourly' : previousRate.rate_type === 'daily' ? 'daily' : 'monthly'}
            {!formData.current_rate && (
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    current_rate: previousRate.rate,
                    currency: previousRate.currency,
                    rate_type: previousRate.rate_type
                  }));
                }}
                className="ml-3 text-blue-600 hover:text-blue-800 underline"
              >
                Use
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rate *
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="number"
              name="current_rate"
              value={formData.current_rate}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.current_rate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.current_rate && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.current_rate}
            </p>
          )}
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Currency
          </label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="PLN">PLN (zł)</option>
            <option value="EUR">EUR (€)</option>
            <option value="USD">USD ($)</option>
            <option value="GBP">GBP (£)</option>
            <option value="NOK">NOK (kr)</option>
            <option value="SEK">SEK (kr)</option>
            <option value="DKK">DKK (kr)</option>
            <option value="CHF">CHF (Fr)</option>
          </select>
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rate Type
          </label>
          <select
            name="rate_type"
            value={formData.rate_type}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {/* Employment Details */}
      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Employment Details</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Position
            </label>
            <input
              type="text"
              name="job_position"
              value={formData.job_position}
              onChange={handleChange}
              placeholder="e.g. Software Developer"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Work Location
            </label>
            <input
              type="text"
              name="work_location"
              value={formData.work_location}
              onChange={handleChange}
              placeholder="e.g. Warsaw, Poland / Remote"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Working Hours
            </label>
            <input
              type="text"
              name="working_hours"
              value={formData.working_hours}
              onChange={handleChange}
              placeholder="e.g. Full-time 40h/week"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vacation Days (per year)
            </label>
            <input
              type="number"
              name="vacation_days"
              value={formData.vacation_days}
              onChange={handleChange}
              placeholder="e.g. 26"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notice Period
            </label>
            <input
              type="text"
              name="notice_period"
              value={formData.notice_period}
              onChange={handleChange}
              placeholder="e.g. 30 days"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Additional Rates */}
      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Additional Rates</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rate Travel
            </label>
            <input
              type="number"
              step="0.01"
              name="rate_travel"
              value={formData.rate_travel}
              onChange={handleChange}
              placeholder="e.g. 50.00"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rate Training
            </label>
            <input
              type="number"
              step="0.01"
              name="rate_training"
              value={formData.rate_training}
              onChange={handleChange}
              placeholder="e.g. 30.00"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Custom Rates */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Rates
          </label>

          {/* Existing custom rates */}
          {Object.keys(formData.custom_rates).length > 0 && (
            <div className="space-y-2 mb-3">
              {Object.entries(formData.custom_rates).map(([name, value]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 w-32 truncate">{name}:</span>
                  <input
                    type="number"
                    step="0.01"
                    value={value}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        custom_rates: {
                          ...prev.custom_rates,
                          [name]: e.target.value
                        }
                      }));
                    }}
                    placeholder="0.00"
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newRates = { ...formData.custom_rates };
                      delete newRates[name];
                      setFormData(prev => ({ ...prev, custom_rates: newRates }));
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new custom rate */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              id="new_custom_rate_name"
              placeholder="Rate name"
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <button
              type="button"
              onClick={() => {
                const input = document.getElementById('new_custom_rate_name');
                const name = input.value.trim();
                if (name) {
                  setFormData(prev => ({
                    ...prev,
                    custom_rates: {
                      ...prev.custom_rates,
                      [name]: ''
                    }
                  }));
                  input.value = '';
                }
              }}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Bank Details (for payments)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Name
            </label>
            <input
              type="text"
              name="bank_name"
              value={formData.bank_name}
              onChange={handleChange}
              placeholder="e.g. PKO Bank Polski"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Number / IBAN
            </label>
            <input
              type="text"
              name="bank_account"
              value={formData.bank_account}
              onChange={handleChange}
              placeholder="e.g. PL61 1090 1014 0000 0712 1981 2874"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        Contract Content & Signatures
      </h3>

      {/* Template Selection */}
      {templates.length > 0 && (
        <div className={`border rounded-lg p-4 ${contract ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'}`}>
          <label className={`block text-sm font-medium mb-2 ${contract ? 'text-gray-700' : 'text-blue-900'}`}>
            Szablon umowy / Contract Template
          </label>
          {contract ? (
            <>
              <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-600">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span>
                  {templates.find(t => t.id === selectedTemplateId)?.name || 'Brak szablonu / No template'}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-600">
                Szablon nie może być zmieniony podczas edycji istniejącej umowy. Jeśli potrzebujesz innego szablonu, utwórz nową umowę.
                <br />
                <em>Template cannot be changed when editing an existing contract. If you need a different template, create a new contract.</em>
              </p>
            </>
          ) : (
            <>
              <select
                value={selectedTemplateId || ''}
                onChange={(e) => setSelectedTemplateId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.template_type_display})
                    {template.is_default ? ' - Domyślny / Default' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-blue-700">
                Szablon określa formatowanie dokumentu PDF. Możesz tworzyć szablony w zakładce Szablony.
                <br />
                <em>Template defines how the PDF document will be formatted. You can create templates in the Templates tab.</em>
              </p>
            </>
          )}
        </div>
      )}

      {/* Signatures */}
      <div className="border-t pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Signatures</h4>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employer Name
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                name="employer_signature_name"
                value={formData.employer_signature_name}
                onChange={handleChange}
                placeholder="e.g. John Smith"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employer Title
            </label>
            <input
              type="text"
              name="employer_signature_title"
              value={formData.employer_signature_title}
              onChange={handleChange}
              placeholder="e.g. General Director"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name
            </label>
            <div className="relative">
              <UsersIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                name="employee_signature_name"
                value={formData.employee_signature_name}
                onChange={handleChange}
                placeholder="e.g. Anna Smith"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signature Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="date"
                name="signature_date"
                value={formData.signature_date}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signature Place
            </label>
            <input
              type="text"
              name="signature_place"
              value={formData.signature_place}
              onChange={handleChange}
              placeholder="e.g. Warsaw, Poland"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Administrative Notes
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Notes visible only to administrators..."
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );

  const renderStep4 = () => {
    const selectedUser = getSelectedUser();

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Summary
        </h3>

        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          {/* Basic Info */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 pb-2 border-b">Basic Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">User:</span>
                <span className="ml-2 font-medium">
                  {selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Contract number:</span>
                <span className="ml-2 font-medium">{formData.contract_number}</span>
              </div>
              <div>
                <span className="text-gray-600">Creation date:</span>
                <span className="ml-2 font-medium">
                  {formData.creation_date ? new Date(formData.creation_date).toLocaleDateString() : '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Start date:</span>
                <span className="ml-2 font-medium">
                  {formData.start_date ? new Date(formData.start_date).toLocaleDateString() : '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">End date:</span>
                <span className="ml-2 font-medium">
                  {formData.end_date ? new Date(formData.end_date).toLocaleDateString() : 'Indefinite'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <span className="ml-2 font-medium capitalize">{formData.status}</span>
              </div>
              {selectedTemplateId && templates.length > 0 && (
                <div>
                  <span className="text-gray-600">Template:</span>
                  <span className="ml-2 font-medium">
                    {templates.find(t => t.id === selectedTemplateId)?.name || '-'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Rate Info */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 pb-2 border-b">Compensation</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Rate:</span>
                <span className="ml-2 font-medium text-lg text-blue-600">
                  {parseFloat(formData.current_rate).toFixed(2)} {formData.currency}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Type:</span>
                <span className="ml-2 font-medium capitalize">{formData.rate_type}</span>
              </div>
            </div>
          </div>

          {/* Signatures */}
          {(formData.employer_signature_name || formData.employee_signature_name) && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3 pb-2 border-b">Signatures</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {formData.employer_signature_name && (
                  <div>
                    <span className="text-gray-600">Employer:</span>
                    <span className="ml-2 font-medium">{formData.employer_signature_name}</span>
                    {formData.employer_signature_title && (
                      <span className="ml-1 text-gray-500">({formData.employer_signature_title})</span>
                    )}
                  </div>
                )}
                {formData.employee_signature_name && (
                  <div>
                    <span className="text-gray-600">Employee:</span>
                    <span className="ml-2 font-medium">{formData.employee_signature_name}</span>
                  </div>
                )}
                {formData.signature_date && (
                  <div>
                    <span className="text-gray-600">Signature date:</span>
                    <span className="ml-2 font-medium">
                      {new Date(formData.signature_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {formData.signature_place && (
                  <div>
                    <span className="text-gray-600">Place:</span>
                    <span className="ml-2 font-medium">{formData.signature_place}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          {(formData.contract_text_pl || formData.contract_text_en || formData.pdf_document) && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3 pb-2 border-b">Documents</h4>
              <div className="space-y-2 text-sm">
                {formData.contract_text_pl && (
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Contract text (Polish)</span>
                  </div>
                )}
                {formData.contract_text_en && (
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Contract text (English)</span>
                  </div>
                )}
                {formData.pdf_document && (
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>PDF: {formData.pdf_document.name}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> Upon approval, the contract will be created and visible to administrators.
            You can edit it later or add amendments with rate changes.
          </p>
        </div>
      </div>
    );
  };

  const handleDownloadPDF = () => {
    if (!createdContractId) return;

    // Use template-based PDF if template is selected, otherwise use default
    let downloadUrl = `${API_BASE_URL}/contracts/${createdContractId}/download/`;
    if (selectedTemplateId) {
      downloadUrl = `${API_BASE_URL}/contracts/${createdContractId}/download-template/?template_id=${selectedTemplateId}`;
    }

    fetch(downloadUrl, {
      headers: { Authorization: `Bearer ${user.accessToken}` }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to download PDF');
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contract_${formData.contract_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(err => {
        console.error('Error downloading PDF:', err);
        alert('Error downloading PDF. Make sure the template has been configured.');
      });
  };

  // Success Modal
  if (showSuccessModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Contract Created Successfully!
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Contract <strong>{formData.contract_number}</strong> has been created.
              You can now download the PDF to print.
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>

              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  onSave();
                }}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {contract ? 'Edit Contract' : 'New Contract Wizard'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-6">
          {renderStepIndicator()}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          <div className="text-sm text-gray-600">
            Step {currentStep} of {steps.length}
          </div>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {loading ? 'Saving...' : contract ? 'Update Contract' : 'Create Contract'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractWizard;
